/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useEffect, useMemo } from 'react';
import type { ShopperProducts, ShopperSearch } from '@/scapi';
import { useScapiFetcher } from './use-scapi-fetcher';

type SuggestionResult = ShopperSearch.schemas['SuggestionResult'];
type ProductsResponse = { data?: ShopperProducts.schemas['Product'][] };

/**
 * Fills in missing product suggestion thumbnails.
 *
 * SCAPI search-suggestions returns `image: null` for this catalog because products only carry the
 * `hi-res` view type (see config `search.products.images`), which the suggestion endpoint does not
 * pick up. For products without an image, this issues one batched `getProducts` call and uses the
 * first image of the first image group.
 */
export function useSearchSuggestionImages(
    data: SuggestionResult | null | undefined
): SuggestionResult | null | undefined {
    const missingIds = useMemo(
        () =>
            (data?.productSuggestions?.products ?? [])
                .filter((product) => !product.image && product.productId)
                .map((product) => product.productId),
        [data]
    );
    const idsKey = missingIds.join(',');

    const fetcher = useScapiFetcher('shopperProducts', 'getProducts', {
        params: {
            query: {
                ids: missingIds,
                expand: ['images'],
                allImages: false,
            },
        },
    });

    useEffect(() => {
        if (idsKey) {
            void fetcher.load();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idsKey]);

    const products = (fetcher.data as ProductsResponse | undefined)?.data;

    return useMemo(() => {
        if (!data?.productSuggestions?.products || !products?.length) return data;

        const imageById = new Map<string, ShopperSearch.schemas['Image']>();
        for (const product of products) {
            const image = product.imageGroups?.[0]?.images?.[0];
            if (image) imageById.set(product.id, image as ShopperSearch.schemas['Image']);
        }
        if (imageById.size === 0) return data;

        return {
            ...data,
            productSuggestions: {
                ...data.productSuggestions,
                products: data.productSuggestions.products.map((product) =>
                    product.image ? product : { ...product, image: imageById.get(product.productId) ?? product.image }
                ),
            },
        };
    }, [data, products]);
}
